import {
  RevelateDealType,
  Database,
  DateRange,
  Scope,
  CalculationDeal,
  NestedCommission,
  TokenProvider,
  NestedTeam,
  DealRole,
  BaseCompany,
  NestedDeal,
  NestedDealStalled,
} from "@revelate/types";
import {
  addMonths,
  areIntervalsOverlapping,
  differenceInCalendarWeeks,
  differenceInMonths,
  endOfDay,
  isWithinInterval,
  startOfDay,
  subHours,
} from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import { getExchangeRateForDeal } from "./currencies";
import { isMultipleOf } from "@revelate/utils";
import { getUserIdsForTeam } from "./teams";

export type CalculationDealStalled = CalculationDeal & { comment?: string };

export const isInvoice = (deal: CalculationDeal | NestedDeal) => {
  return deal.provider === "fortnox";
};

export const sortDealsByAmount = (
  deals: (Database["public"]["Tables"]["deals"]["Row"] | NestedDeal)[]
) => {
  return deals.sort((a, b) => (a.value || 0) - (b.value || 0));
};

export const getDealValue = (
  company: BaseCompany | null,
  deal: CalculationDeal,
  useOnboardingValue: boolean | null = false,
  logs?: string[]
) => {
  return (
    (useOnboardingValue ? deal.onboarding_value || 0 : deal.value || 0) *
    getExchangeRateForDeal(company, deal, logs)
  );
};

export const getDealsValue = (
  company: BaseCompany | null,
  deals: CalculationDeal[],
  useOnboardingValue: boolean | null = false,
  logs?: string[]
) => {
  return (
    deals?.reduce(
      (acc, deal) =>
        acc + getDealValue(company, deal, useOnboardingValue, logs),
      0
    ) || 0
  );
};

export const getAverageDealValue = (
  company: BaseCompany | null,
  deals: CalculationDeal[]
) => {
  const totalValue = getDealsValue(company, deals);
  return totalValue / (deals.length || 1);
};

export const getDealsForScope = ({
  deals,
  scope,
  teams,
}: {
  deals: NestedDeal[] | CalculationDeal[];
  scope?: Scope | null;
  teams?: NestedTeam[];
}) => {
  if (!scope) return deals;

  const { type } = scope;

  // FIXME: Doesn't this filter out collaborators?
  if (type === "user") {
    return deals.filter((deal) => getDealOwnerId(deal) === scope.value);
  }
  if (teams && type === "team") {
    const userIds = getUserIdsForTeam(teams, Number(scope.value), true);
    return deals.filter(
      (deal) =>
        getDealOwnerId(deal) && userIds.includes(getDealOwnerId(deal) || "")
    );
  }
  return deals;
};

const filterDealByDateRange = ({
  deals,
  dateRange,
  isProjected = false,
}: {
  deals: CalculationDeal[];
  dateRange: DateRange;
  isProjected?: boolean;
}) => {
  const { from, to } = dateRange || {};
  if (!from || !to) return deals;
  const today = new Date();

  // Adjust range boundaries to handle end-of-month deals
  // Subtract 2 hours from the start of day to ensure deals after 22:00 UTC are counted in the next month
  const fromUTC = fromZonedTime(subHours(startOfDay(new Date(from)), 2), "UTC");
  const toUTC = fromZonedTime(subHours(endOfDay(new Date(to)), 2), "UTC");

  const filteredDeals = deals.filter((deal) => {
    // Convert deal closed_at to UTC and adjust time to match our boundary logic
    const dealClosedAtUTC = deal.closed_at
      ? fromZonedTime(new Date(deal.closed_at), "UTC")
      : null;

    // Projected deals are assigned an expected closing date by the CRM system that we can use here
    const isClosedWithinRange =
      dealClosedAtUTC &&
      isWithinInterval(dealClosedAtUTC, {
        start: fromUTC,
        end: toUTC,
      });

    const nextInvoiceDueWithinRange =
      dealClosedAtUTC &&
      deal.contract_length_months &&
      deal.customer_deal_count === 1 && // Only count the first invoice
      // Contract is still live - using UTC dates for comparison
      areIntervalsOverlapping(
        {
          start: fromZonedTime(subHours(startOfDay(dealClosedAtUTC), 2), "UTC"),
          end: fromZonedTime(
            subHours(
              endOfDay(addMonths(dealClosedAtUTC, deal.contract_length_months)),
              2
            ),
            "UTC"
          ),
        },
        {
          start: fromUTC,
          end: toUTC,
        }
      );

    const nextInvoiceDueThisMonth =
      dealClosedAtUTC &&
      deal.invoice_length_months &&
      differenceInMonths(today, dealClosedAtUTC) > 0 &&
      isMultipleOf(
        differenceInMonths(today, dealClosedAtUTC),
        deal.invoice_length_months
      );

    return isProjected && isInvoice(deal)
      ? nextInvoiceDueWithinRange && nextInvoiceDueThisMonth
      : isClosedWithinRange;
  });

  return filteredDeals;
};

export const filterByDealRole = (
  deals: CalculationDeal[],
  dealRole: DealRole,
  userId: string,
) => {
  // All deal conditions
  if (dealRole === "management" || dealRole === "all_deals") return deals;

  // Otherwise filter by given role (owner/collaborator)
  const filteredByRole = deals.filter((deal) => {
    const dealUser = deal?.users?.find(
      (dealUser) => dealUser?.user_id === userId
    );
    const dealMatchesRoleRequirement = dealUser && dealUser?.role === dealRole;
    return dealMatchesRoleRequirement;
  });

  return filteredByRole;
};

export function filterByProviders(deals: CalculationDeal[], providers?: TokenProvider[] | null) {
  if (!providers || !providers.length) return deals;

  const uniqueProviders = new Set(providers)
  return deals.filter((deal) => uniqueProviders.has(deal.provider))
}

export function filterByPipelines(deals: CalculationDeal[], pipelines?: string[]) {
  if (!pipelines || !pipelines.length) return deals;

  const uniquePipelines = new Set(pipelines)
  return deals.filter((deal) => deal.pipeline && uniquePipelines.has(deal.pipeline.toString()))
}

export function filterByDealTypes(deals: CalculationDeal[], dealTypes?: RevelateDealType[]) {
  if (!dealTypes || !dealTypes.length) return deals;

  const dealTypeSet = new Set(dealTypes);
  return deals.filter(deal =>
    dealTypeSet.has(deal.deal_type as RevelateDealType)
  );
}

export function filterByStatus(deals: CalculationDeal[], isProjected = false) {
  return deals.filter(deal => {
    const isInv = isInvoice(deal);

    // TODO: Extract to function
    const correctStatusForProjections =
      isInv || (!deal.is_won && !deal.is_closed);

    // TODO: Extract to function
    const correctStatusForNonProjection =
      deal.is_closed && deal.is_won;

    const hasCorrectStatus = isProjected
      ? correctStatusForProjections
      : correctStatusForNonProjection;

    return hasCorrectStatus;
  });
}

// Create a date range map for efficient lookups
function createDateRangeMap(dateRange: DateRange) {
  const map = new Map<string, boolean>();
  const start = startOfDay(dateRange.from ? new Date(dateRange.from) : new Date());
  const end = endOfDay(dateRange.to ? new Date(dateRange.to) : new Date());

  let current = start;
  while (current <= end) {
    map.set(
      `${current.getFullYear()}-${current.getMonth() + 1}`,
      true
    );
    current = addMonths(current, 1);
  }

  return { map, start, end };
}

// TODO: Rename to something like filterDeals()
// Because this applies a lot of filters, not just date range
// And infer the return type from the input deals type
// (also goes for all related functions inside)
export const getDealsForDateRange = ({
  deals: inputDeals,
  dateRange,
  scope,
  teams,
  dealTypes,
  providers,
  pipelines,
  dealRole,
  userId,
  isProjected = false,
}: {
  deals: CalculationDeal[] | NestedDeal[];
  dateRange: DateRange;
  scope?: Scope | null;
  teams?: NestedTeam[];
  dealTypes?: RevelateDealType[];
  providers?: TokenProvider[];
  pipelines?: string[];
  dealRole?: DealRole;
  userId?: string;
  isProjected?: boolean;
}) => {
  let deals = inputDeals;

  deals = filterByProviders(deals, providers);

  deals = filterByPipelines(deals, pipelines);

  // Filter by date range
  // TODO: Extract to filterByDateRange(deals, dateRange)
  const { map: dateRangeMap, start, end } = createDateRangeMap(dateRange);
  const today = new Date();

  const uniqueProviders = providers && providers.length
    ? new Set(providers)
    : null;

  // TODO: Is this check needed here?
  const isProjectedAndHasFortnoxDeals =
    isProjected && (!providers || uniqueProviders?.has('fortnox'));

  deals = deals.filter(deal => {
    const dealDate = deal.closed_at
      ? new Date(deal.closed_at)
      : new Date(deal.created_at);

    // Quick reject if outside date range
    if (dealDate < start || dealDate > end) {
      return false;
    }

    const dealKey = `${dealDate.getFullYear()}-${dealDate.getMonth() + 1}`;

    // Fortnox invoices have periodic payout intervals (invoice_length_months)
    // Check if the current month is a payout month, then we include it
    if (isProjectedAndHasFortnoxDeals && deal.provider === 'fortnox') {
      if (!deal.invoice_length_months) return false;
      const monthDiff = differenceInMonths(today, dealDate);
      return monthDiff > 0 &&
        isMultipleOf(monthDiff, deal.invoice_length_months) &&
        dateRangeMap.has(dealKey);
    }

    return dateRangeMap.has(dealKey);
  });

  if (scope || teams) {
    deals = getDealsForScope({ deals, scope, teams });
  }

  deals = filterByDealTypes(deals, dealTypes);

  if (dealRole && userId) {
    deals = filterByDealRole(deals, dealRole, userId);
  }

  deals = filterByStatus(deals, isProjected);

  // Sort in chronological order (oldest to newest)
  return deals.sort((a, b) => {
    const aDate = new Date(a.closed_at || a.created_at);
    const bDate = new Date(b.closed_at || b.created_at);
    return aDate.getTime() - bDate.getTime();
  });
};

export const getDealValueFromCommissions = (
  commissions: NestedCommission[]
) => {
  if (!commissions || commissions.length === 0) return 0;
  return commissions?.reduce(
    (acc, commission) => commission.deal?.value ?? acc ?? 0, // don't accumulate, just take a found value
    0
  );
};

export const getStalledDeals = (
  projectedDeals: NestedDeal[],
  limit: number = 3
): NestedDealStalled[] => {
  return projectedDeals
    .map(mapStalledDealWithReason)
    .filter((d) => d !== null)
    .sort((a, b) => (b.value || 0) - (a.value || 0))
    .slice(0, limit);
};

const mapStalledDealWithReason = (
  deal: NestedDeal
): NestedDealStalled | null => {
  const today = new Date();

  const sameStageDurationInWeeks = deal.next_step_updated_at
    ? differenceInCalendarWeeks(today, deal.next_step_updated_at)
    : undefined;

  const isNextStepAssigned = deal.next_step && deal.next_step.length > 0;

  const lastUpdatedAgoInWeeks = deal.last_activity_at
    ? differenceInCalendarWeeks(today, deal.last_activity_at)
    : undefined;

  if (sameStageDurationInWeeks && sameStageDurationInWeeks > 3) {
    return {
      ...deal,
      comment: `Same stage for ${sameStageDurationInWeeks} weeks`,
    };
  }

  if (!isNextStepAssigned) {
    return { ...deal, comment: "No next step assigned" };
  }

  if (lastUpdatedAgoInWeeks && lastUpdatedAgoInWeeks > 2) {
    return { ...deal, comment: `No updates in ${lastUpdatedAgoInWeeks} weeks` };
  }

  return null;
};

export const getDealOwnerId = (deal: NestedDeal | CalculationDeal) => {
  const owner = deal?.users?.find((user) => user.role === "owner");
  return owner?.user_id || null;
};

export const getDealOwner = (deal: NestedDeal) => {
  const owner = deal?.users?.find((user) => user.role === "owner");
  return owner?.data || null;
};

export const isUserDealOwner = (deal: NestedDeal | CalculationDeal, userId: string) => {
  return deal?.users?.some((user) =>
    user.user_id === userId &&
    user.role === "owner"
  );
}

export const isUserDealCollaborator = (deal: NestedDeal | CalculationDeal, userId: string) => {
  return deal?.users?.some((user) =>
    user.user_id === userId &&
    user.role === "collaborator"
  );
}

// FIXME: Works on all db entities, not just deals. Can rename.
export const getUniqueDeals = <T extends { id: number }>(deals: T[]) => {
  const uniqueDealIds = new Set<number>();
  const uniqueDeals: T[] = [];

  for (const deal of deals) {
    if (!uniqueDealIds.has(deal.id)) {
      uniqueDealIds.add(deal.id);
      uniqueDeals.push(deal);
    }
  }

  return uniqueDeals;
}
